// The Publish page's side of compiling (design §2.6; protocol in ../publish/protocol.ts): it starts the compile worker
// when Publish opens and ends it (and the Typst worker) when the writer leaves, keeps the worker's copy of the documents
// up to date before each request (only what changed: the changes since, or the whole document when its history was
// replaced), records each compile's checkpoint as its own edit (so it syncs like any other), and typesets for the
// worker through the one Typst worker, which it starts on the first PDF. `lastPrint-<project>` (what the last print
// build fixed and its page count) is kept in this browser, as the Mac keeps it in UserDefaults.
import { storageId } from './storageId.ts';
import * as A from '@automerge/automerge';
import type { LastPrint, MarketRules, PreflightFinding } from '@writing-suite/compile';
import type { TypstEngine } from '@writing-suite/compile/typst';
import {
  transferables, type BuildResult, type Call, type DocUpdate, type FormatList, type FromHost, type Port, type Preparation, type Preview,
  type Progress, type Request, type Selection, type ToHost, type Writer,
} from '../publish/protocol.ts';
import { placeCopyfitNotes, type CopyfitPlacement } from './copyfit.ts';

/** The compile worker as the page holds it. */
export interface HostConnection {
  port: Port<FromHost, ToHost>;
  terminate(): void;
}

/** The Typst client (`Typst` from @writing-suite/typst) as the session uses it. */
export type TypstClient = TypstEngine & { terminate(): void };

/** What the session needs of the live project. */
export interface LiveDocuments {
  documents(): Map<string, A.Doc<unknown>>;
  edit<T>(fn: (editor: { createCheckpoint(name: string, trigger?: string): { id: string } }) => T): T;
  editDocuments<T>(fn: (docs: Map<string, A.Doc<unknown>>) => T): T;
}

export interface PublishDeps {
  live: LiveDocuments;
  /** Starts the compile worker. */
  connect(): HostConnection;
  /** Starts the Typst worker; null when this build has no PDF engine (`SKIP_TYPST`). */
  typst: (() => TypstClient) | null;
  device: string;
}

export class PublishError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PublishError';
  }
}

interface Pending {
  resolve(value: unknown): void;
  reject(error: Error): void;
  progress?(p: Progress): void;
}

export class PublishSession {
  private host: HostConnection | null = null;
  private typstClient: TypstClient | null = null;
  private sent = new Map<string, A.Heads>();
  private pending = new Map<number, Pending>();
  private nextId = 1;
  private disposed = false;
  /** Typst workers started (tests; the page ends them on leaving). */
  typstStarted = 0;

  constructor(private readonly deps: PublishDeps) {}

  get hasPDF(): boolean {
    return this.deps.typst !== null;
  }

  /** Whether the Typst worker is running now. */
  get typstRunning(): boolean {
    return this.typstClient !== null;
  }

  private connection(): HostConnection {
    if (this.disposed) throw new PublishError('Publish was closed.');
    if (!this.host) {
      const host = this.deps.connect();
      host.port.listen((m) => this.receive(m));
      host.port.post({ t: 'init', typst: this.hasPDF });
      this.host = host;
    }
    return this.host;
  }

  /** Sends the worker what changed in the documents since it last had them. */
  sync(): void {
    const host = this.connection();
    const docs = this.deps.live.documents();
    const updates: DocUpdate[] = [];
    for (const [name, doc] of docs) {
      const heads = A.getHeads(doc);
      const had = this.sent.get(name);
      if (had && had.length === heads.length && had.every((h, i) => h === heads[i])) continue;
      // Changes since, unless the history the worker has isn't in this one (a compacted epoch replaced it).
      const incremental = had !== undefined && A.hasHeads(doc, had);
      updates.push({ name, bytes: incremental ? A.saveSince(doc, had) : A.save(doc), full: !incremental });
      this.sent.set(name, heads);
    }
    const removed = [...this.sent.keys()].filter((n) => !docs.has(n));
    for (const n of removed) this.sent.delete(n);
    if (updates.length || removed.length) host.port.post({ t: 'sync', updates, removed }, transferables(updates));
  }

  private request<T>(request: Request, progress?: (p: Progress) => void): Promise<T> {
    let host: HostConnection;
    try {
      host = this.connection();
      this.sync();
    } catch (e) {
      return Promise.reject(e);
    }
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject, progress });
      host.port.post({ t: 'request', id, request });
    });
  }

  private receive(m: FromHost) {
    if (this.disposed) return;
    switch (m.t) {
      case 'response': {
        const p = this.pending.get(m.id);
        if (!p) return;
        this.pending.delete(m.id);
        if (m.ok) p.resolve(m.result);
        else p.reject(new PublishError(m.error));
        return;
      }
      case 'progress':
        this.pending.get(m.id)?.progress?.(m.progress);
        return;
      case 'call':
        void this.answer(m.id, m.call);
        return;
    }
  }

  private async answer(id: number, call: Call) {
    const reply = (msg: ToHost, transfer?: Transferable[]) => { if (!this.disposed) this.host?.port.post(msg, transfer); };
    try {
      if (call.kind === 'checkpoint') {
        // `createCheckpoint(… trigger: .compile)`, as this device's edit; the worker gets the project document that
        // records it before the answer, so it can tell whether the text moved on meanwhile.
        const made = this.deps.live.edit((e) => e.createCheckpoint(call.name, 'compile'));
        this.sync();
        return reply({ t: 'reply', id, ok: true, result: made.id });
      }
      const typst = this.typst();
      const fn = typst[call.op] as (...args: unknown[]) => Promise<unknown>;
      const result = await fn.apply(typst, call.args);
      reply({ t: 'reply', id, ok: true, result }, transferables(result));
    } catch (e) {
      reply({ t: 'reply', id, ok: false, error: e instanceof Error ? e.message : String(e) });
    }
  }

  private typst(): TypstClient {
    if (!this.deps.typst) throw new PublishError('PDF isn’t in this build.');
    if (!this.typstClient) {
      this.typstClient = this.deps.typst();
      this.typstStarted++;
    }
    return this.typstClient;
  }

  formats(): Promise<FormatList> {
    return this.request({ kind: 'formats' });
  }

  prepare(s: Selection, lastPrint: LastPrint | null): Promise<Preparation> {
    return this.request({ kind: 'prepare', ...s, lastPrint });
  }

  preview(s: Selection, cachedKey: string | null): Promise<{ key: string; preview: Preview | null }> {
    return this.request({ kind: 'preview', ...s, cachedKey });
  }

  /** `limit`: only these fragments (Publish ▸ only the filtered scenes), with the folders above them as headings. */
  build(s: Selection, o: { market?: MarketRules | null; locale?: string; limit?: string[] | null; progress?: (p: Progress) => void } = {}): Promise<BuildResult> {
    return this.request({ kind: 'build', ...s, market: o.market ?? null, locale: o.locale, limit: o.limit ?? null }, o.progress);
  }

  pack(presetIds: string[], writer: Writer, o: { locale?: string } = {}): Promise<BuildResult> {
    return this.request({ kind: 'pack', presetIds, writer, locale: o.locale });
  }

  /** Puts a print build's copyfitting suggestions in the margin (`addCopyfitNotes`). */
  addCopyfitNotes(findings: readonly PreflightFinding[], checkpointId: string | null): CopyfitPlacement {
    return this.deps.live.editDocuments((docs) => placeCopyfitNotes(docs, findings, { device: this.deps.device, checkpointId }));
  }

  /** Ends both workers (their memory goes back at once); anything still waiting fails. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.host?.terminate();
    this.host = null;
    this.typstClient?.terminate();
    this.typstClient = null;
    for (const p of this.pending.values()) p.reject(new PublishError('Publish was closed.'));
    this.pending.clear();
  }
}

// MARK: The last print build (`lastPrint-<project>`, per browser)

export function readLastPrint(projectId: string): LastPrint | null {
  try {
    const raw = localStorage.getItem(`lastPrint-${storageId(projectId)}`);
    if (!raw) return null;
    const v = JSON.parse(raw) as LastPrint;
    return typeof v.pages === 'number' && typeof v.ornamentedBreaks === 'number' && typeof v.loosenedParagraphs === 'number' ? v : null;
  } catch {
    return null;
  }
}

export function recordLastPrint(projectId: string, last: LastPrint): void {
  try {
    localStorage.setItem(`lastPrint-${storageId(projectId)}`, JSON.stringify(last));
  } catch {
    // kept for this page only
  }
}
